import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { KIA_LOGO } from "@/data/site";

const LINKS = [
  { label: "Lineup", href: "#lineup", testid: "nav-link-lineup" },
  { label: "Why Sanjib", href: "#manifesto", testid: "nav-link-manifesto" },
  { label: "EMI", href: "#emi", testid: "nav-link-emi" },
  { label: "Service", href: "#service", testid: "nav-link-service" },
  { label: "Visit Us", href: "#visit", testid: "nav-link-visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled
          ? "bg-[#05141F]/80 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <img
            src={KIA_LOGO}
            alt="Kia"
            data-testid="nav-kia-logo"
            className="h-6 sm:h-7 w-auto invert mix-blend-screen"
          />
          <span className="w-px h-5 bg-white/20" />
          <span className="font-display font-extrabold tracking-[0.18em] text-white text-sm sm:text-base">
            SANJIB <span className="text-[#FFFFFF]">KIA</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-[13px] font-mono uppercase tracking-[0.22em] text-slate-300 hover:text-white transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[#FFFFFF] hover:after:w-full after:transition-[width] after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden lg:flex items-center gap-1.5 text-[11px] font-mono tracking-[0.15em] uppercase text-slate-400 border border-white/10 rounded-full px-3.5 py-1.5">
            <MapPin size={12} className="text-[#FFFFFF]" /> Jajpur, Odisha
          </span>
          <button
            data-testid="nav-book-test-drive-button"
            onClick={() => go("#test-drive")}
            className="hidden sm:inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.18em] px-5 py-2.5 clip-notch transition-colors duration-300"
          >
            Book Test Drive
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#05141F]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl text-white tracking-tight"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-book-test-drive-button-mobile"
                onClick={() => go("#test-drive")}
                className="mt-2 bg-[#FFFFFF] text-black text-sm font-semibold uppercase tracking-[0.18em] px-5 py-3.5 clip-notch"
              >
                Book Test Drive
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
